/**
 * 公開故事整理與作者授權的純函式模型。
 * 規則：選入／編輯／授權需等下次發布更新才生效；取消選入／撤回授權立即停止公開顯示。
 */

/** 文案 key 前綴；此模型只回傳 key，由呈現層依 app locale 翻譯。 */
const S = 'travel.publication.story';

export const STORY_TIMING_NOTICE_KEY = `${S}.timing`;

const CONSENT_VALUES = new Set(['PENDING', 'GRANTED', 'REVOKED']);
const PUBLISHABLE_VISIBILITIES = new Set(['PUBLIC', 'FOLLOWERS']);

const idOf = (value) => (value === null || value === undefined || value === '' ? null : String(value));

function revisionOf(value) {
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export function normalizeStoryRevision(raw) {
  return {
    publicationId: idOf(raw?.publicationId),
    storyRevision: revisionOf(raw?.storyRevision),
  };
}

export function normalizeOwnerStory(raw) {
  const items = Array.isArray(raw?.items) ? raw.items : [];
  return {
    publicationId: idOf(raw?.publicationId),
    storyRevision: revisionOf(raw?.storyRevision),
    items: items
      .map((item) => ({
        memoryEntryId: idOf(item?.memoryEntryId),
        authoredByOwner: item?.authoredByOwner === true,
        memoryType: String(item?.memoryType || ''),
        title: item?.title ? String(item.title) : '',
        ownerSelected: item?.ownerSelected === true,
        displayOrder: Number.isInteger(item?.displayOrder) ? item.displayOrder : null,
        authorConsent: CONSENT_VALUES.has(item?.authorConsent) ? item.authorConsent : 'PENDING',
        publishableNow: item?.publishableNow === true,
      }))
      .filter((item) => item.memoryEntryId),
  };
}

export function normalizeAuthorConsent(raw) {
  return {
    memoryEntryId: idOf(raw?.memoryEntryId),
    publicationExists: raw?.publicationExists === true,
    authoredByOwner: raw?.authoredByOwner === true,
    authorConsent: CONSENT_VALUES.has(raw?.authorConsent) ? raw.authorConsent : 'PENDING',
    ownerSelected: raw?.ownerSelected === true,
  };
}

/** 「我的 → 公開故事授權」分頁；ID 一律字串。 */
export function normalizeMyConsentPage(raw) {
  const items = Array.isArray(raw?.items) ? raw.items : [];
  return {
    items: items
      .map((item) => ({
        journeyId: idOf(item?.journeyId),
        publicationId: idOf(item?.publicationId),
        memoryEntryId: idOf(item?.memoryEntryId),
        memoryType: String(item?.memoryType || ''),
        memoryTitle: item?.memoryTitle ? String(item.memoryTitle) : '',
        memoryStatus: String(item?.memoryStatus || ''),
        occurredAt: item?.occurredAt ? String(item.occurredAt) : null,
        authorConsent: CONSENT_VALUES.has(item?.authorConsent) ? item.authorConsent : 'PENDING',
        ownerSelected: item?.ownerSelected === true,
        consentChangedAt: item?.consentChangedAt ? String(item.consentChangedAt) : null,
        publicJourneyTitle: item?.publicJourneyTitle ? String(item.publicJourneyTitle) : '',
      }))
      .filter((item) => item.journeyId && item.memoryEntryId),
    nextCursorItemId: idOf(raw?.nextCursorItemId),
  };
}

/** 只有可公開、未隱藏的日記型回憶可作為新的收錄候選；已有收錄紀錄者一律以伺服器資料為準。 */
function isCandidateMemory(entry) {
  return Boolean(entry?.memoryEntryId)
    && entry.status === 'ACTIVE'
    && entry.memoryType !== 'PRIVATE_NOTE'
    && PUBLISHABLE_VISIBILITIES.has(entry.visibility);
}

/**
 * 合併伺服器收錄清單與行程回憶清單。
 * 已選入者依 displayOrder 排序；其餘依回憶清單順序。
 */
export function mergeStoryCandidates(ownerStory, memories, { ownerMemberId } = {}) {
  const byId = new Map((memories || []).filter((m) => m?.memoryEntryId).map((m) => [String(m.memoryEntryId), m]));
  const seen = new Set();
  const rows = [];
  (ownerStory?.items || []).forEach((item) => {
    seen.add(item.memoryEntryId);
    const memory = byId.get(item.memoryEntryId) || null;
    rows.push({
      ...item,
      title: item.title || memory?.title || '',
      summary: memory?.contentPreview || '',
      hasRecord: true,
    });
  });
  (memories || []).forEach((memory) => {
    const id = memory?.memoryEntryId ? String(memory.memoryEntryId) : null;
    if (!id || seen.has(id) || !isCandidateMemory(memory)) return;
    seen.add(id);
    const authoredByOwner = Boolean(ownerMemberId) && String(memory.authorMemberId) === String(ownerMemberId);
    rows.push({
      memoryEntryId: id,
      authoredByOwner,
      memoryType: memory.memoryType || 'JOURNAL',
      title: memory.title || '',
      summary: memory.contentPreview || '',
      ownerSelected: false,
      displayOrder: null,
      authorConsent: 'PENDING',
      publishableNow: null,
      hasRecord: false,
    });
  });
  const selected = rows
    .filter((row) => row.ownerSelected)
    .sort((a, b) => (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER));
  const unselected = rows.filter((row) => !row.ownerSelected);
  return { selected, unselected };
}

/** 回傳交換位置後的完整已選入 ID 清單；越界回傳 null。 */
export function moveSelected(selectedIds, index, delta) {
  const target = index + delta;
  if (!Array.isArray(selectedIds) || index < 0 || index >= selectedIds.length
    || target < 0 || target >= selectedIds.length) {
    return null;
  }
  const next = selectedIds.map(String);
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function memoryTypeLabelKey(type) {
  if (type === 'JOURNAL' || type === 'PRIVATE_NOTE') return `${S}.type.${type}`;
  return `${S}.type.default`;
}

export function consentLabelKey(row) {
  if (row.authoredByOwner) return `${S}.consentState.owner`;
  if (row.authorConsent === 'GRANTED' || row.authorConsent === 'REVOKED') return `${S}.consentState.${row.authorConsent}`;
  return `${S}.consentState.PENDING`;
}

export function publishableLabelKey(row) {
  if (row.publishableNow === true) return `${S}.publishable.yes`;
  if (row.publishableNow === false) return row.ownerSelected ? `${S}.publishable.selectedHidden` : `${S}.publishable.ineligible`;
  return `${S}.publishable.unknown`;
}

export function storyErrorKind(cause) {
  const httpStatus = Number(cause?.httpStatus || 0);
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || '');
  const message = String(cause?.raw?.message || cause?.message || '');
  if (httpStatus === 401 || code === 90003 || code === 90005 || code === 90006
    || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (httpStatus === 403 || code === 95101 || key === 'travel.publication.story.forbidden') return 'forbidden';
  if (httpStatus === 404 || code === 95102 || key === 'travel.publication.story.not_found') return 'not-found';
  if (httpStatus === 409 || code === 95103 || key === 'travel.publication.story.stale') return 'conflict';
  if (httpStatus === 422 || code === 95105 || key === 'travel.publication.story.ineligible') return 'ineligible';
  if (httpStatus === 400 || code === 95100 || key === 'travel.publication.story.invalid') return 'invalid';
  if (cause?.networkCode || (!httpStatus && !cause?.code && !cause?.raw)) return 'network';
  if (!cause?.raw && /network|timeout|failed to fetch/i.test(message)) return 'network';
  return 'error';
}

export function storyErrorMessageKey(kind, { context = 'curation' } = {}) {
  const scope = context === 'consent' ? 'consent' : 'curation';
  switch (kind) {
    case 'unauthorized':
      return `${S}.error.unauthorized`;
    case 'forbidden':
      return `${S}.error.forbidden.${scope}`;
    case 'not-found':
      return `${S}.error.notFound.${scope}`;
    case 'conflict':
      return `${S}.error.conflict`;
    case 'ineligible':
      return `${S}.error.ineligible.${scope}`;
    case 'invalid':
      return `${S}.error.invalid`;
    case 'network':
      return `${S}.error.network`;
    default:
      return `${S}.error.generic`;
  }
}
